<template>
    <div class="text-center">
        <v-btn color="success" x-large rounded elevation="4" @click="dialog = true">
            <v-icon left>mdi-file-excel</v-icon>
            Importar corredors (Excel)
        </v-btn>

        <v-dialog v-model="dialog" max-width="600px" :persistent="importing">
            <v-card>
                <v-card-title class="success white--text">
                    <v-icon left dark>mdi-file-excel</v-icon>
                    Importar Corredors
                </v-card-title>

                <v-card-text class="pt-6">
                    <div class="text-body-2 mb-4">
                        Columnes que es llegeixen: <b>Dor, Nom, Cognoms, Club, Categoria, Sexe</b>.
                        La resta s'ignoren. Els corredors que ja existeixen (mateix dorsal o mateix nom i
                        cognoms) no es tornen a afegir.
                    </div>

                    <v-file-input
                        v-model="file"
                        accept=".xlsx,.xls,.csv"
                        label="Selecciona el fitxer Excel"
                        prepend-icon="mdi-file-document"
                        outlined
                        dense
                        :disabled="importing"
                        hide-details
                    ></v-file-input>

                    <v-alert
                        v-if="result"
                        :type="result.type"
                        class="mt-4"
                        dense
                        outlined
                    >
                        <div v-for="(line, index) in result.lines" :key="index">{{ line }}</div>
                    </v-alert>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="grey" text :disabled="importing" @click="close">Tancar</v-btn>
                    <v-btn
                        color="success"
                        text
                        :disabled="!file || importing"
                        :loading="importing"
                        @click="importFile"
                    >
                        <v-icon left>mdi-import</v-icon>
                        Importar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import * as XLSX from "xlsx";

// Columna de l'Excel -> camp del corredor
const COLUMNS = {
    dor: "number",
    nom: "name",
    cognoms: "surname",
    club: "club",
    categoria: "category",
    sexe: "sex",
};

// Categoria de l'Excel (normalitzada) -> id de categoria
const CATEGORY_MAP = {
    MINI: "c1", // SENSE PEDALS
    PREBENJAMI: "c2", // PRE-BENJAMINS
    BENJAMI: "c3", // BENJAMINS
    PRINCIPIANT: "c4", // PRINCIPIANTS
    ALEVI: "c5", // ALEVÍ
    INFANTIL: "c6", // INFANTILS
};

// Majúscules, sense accents ni símbols: "Pre-Benjamí" -> "PREBENJAMI"
function normalize(value) {
    return String(value == null ? "" : value)
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "");
}

// Paraules de sexe que poden venir dins la categoria ("Benjamí Masculí", "ALEVI - F")
const SEX_WORDS = [
    "M", "F", "H", "D", "MASC", "FEM", "MASCULI", "FEMENI", "MASCULINA", "FEMENINA",
    "MASCULINO", "FEMENINO", "HOME", "HOMES", "DONA", "DONES", "NEN", "NENS", "NENA",
    "NENES", "NOI", "NOIS", "NOIA", "NOIES",
];

// Categoria sense el sexe: el sexe es pren sempre de la columna "Sexe"
function categoryKey(value) {
    const words = String(value == null ? "" : value)
        .split(/[\s\-_/().,]+/)
        .filter((word) => word && !SEX_WORDS.includes(normalize(word)));
    return normalize(words.join(""));
}

function normalizeSex(value) {
    const sex = normalize(value);
    if (!sex) return "";
    if (["M", "H", "MASCULI", "MASCULINO", "HOME", "NEN", "NOI"].includes(sex)) return "M";
    if (["F", "D", "FEMENI", "FEMENINO", "DONA", "NENA", "NOIA"].includes(sex)) return "F";
    return String(value).trim();
}

export default {
    name: "ImportRacers",
    data() {
        return {
            dialog: false,
            file: null,
            importing: false,
            result: null,
        };
    },
    computed: {
        categories() {
            return Object.values(this.$store.getters["categories/getCategories"]());
        },
    },
    methods: {
        close() {
            this.dialog = false;
            this.file = null;
            this.result = null;
        },

        findCategory(value) {
            const key = categoryKey(value);
            if (!key) return null;

            const id = CATEGORY_MAP[key];
            if (id && this.categories.some((cat) => cat.id === id)) return id;

            // Si l'Excel ja porta el nom exacte de la categoria
            const exact = this.categories.find((cat) => normalize(cat.name) === key);
            return exact ? exact.id : null;
        },

        // Files de la primera fulla com a objectes { number, name, surname, ... }
        async readRows(file) {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: false });

            const headerIndex = rows.findIndex((row) =>
                row.some((cell) => normalize(cell) === "NOM")
            );
            if (headerIndex === -1) {
                throw new Error("No s'ha trobat la fila de capçalera (columna \"Nom\")");
            }

            let positions = {};
            rows[headerIndex].forEach((cell, index) => {
                const field = COLUMNS[normalize(cell).toLowerCase()];
                if (field && positions[field] === undefined) positions[field] = index;
            });

            const missing = Object.keys(COLUMNS).filter((col) => positions[COLUMNS[col]] === undefined);
            if (missing.length) {
                throw new Error("Falten columnes a l'Excel: " + missing.join(", "));
            }

            return rows.slice(headerIndex + 1).map((row) => {
                let racer = {};
                Object.keys(positions).forEach((field) => {
                    racer[field] = String(row[positions[field]] == null ? "" : row[positions[field]]).trim();
                });
                return racer;
            });
        },

        async importFile() {
            if (!this.file) return;
            this.importing = true;
            this.result = null;

            try {
                const rows = await this.readRows(this.file);

                // Corredors que ja hi són, per dorsal i per nom complet
                const existing = Object.values(this.$store.getters["racers/getRacers"]());
                let numbers = new Set(existing.filter((r) => r.number > 0).map((r) => r.number));
                let names = new Set(existing.map((r) => normalize(r.name + r.surname)));

                let added = 0;
                let skipped = 0;
                let unknownCategories = new Set();
                let noCategory = 0;

                for (const row of rows) {
                    if (!row.name && !row.surname && !row.number) continue;

                    const number = parseInt(row.number) || 0;
                    const fullName = normalize(row.name + row.surname);
                    if ((number > 0 && numbers.has(number)) || names.has(fullName)) {
                        skipped++;
                        continue;
                    }

                    const category = this.findCategory(row.category);
                    if (!category) {
                        if (row.category) unknownCategories.add(row.category);
                        noCategory++;
                        continue;
                    }

                    await this.$store.dispatch("racers/addRacer", {
                        number,
                        name: row.name,
                        surname: row.surname,
                        club: row.club,
                        sex: normalizeSex(row.sex),
                        category,
                    });
                    if (number > 0) numbers.add(number);
                    names.add(fullName);
                    added++;
                }

                let lines = [`S'han afegit ${added} corredors.`];
                if (skipped) lines.push(`${skipped} ja existien i s'han ignorat.`);
                if (noCategory) {
                    lines.push(
                        `${noCategory} sense categoria reconeguda` +
                            (unknownCategories.size ? ": " + [...unknownCategories].join(", ") : ".")
                    );
                }
                this.result = { type: noCategory ? "warning" : "success", lines };
                this.file = null;
            } catch (error) {
                console.error("Error important Excel", error);
                this.result = { type: "error", lines: ["Error important el fitxer: " + error.message] };
            } finally {
                this.importing = false;
            }
        },
    },
};
</script>
